import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Celebration animations
const confettiFall = keyframes`
  0% {
    transform: translateY(-100vh) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`;

const bounceIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Confetti pieces
const ConfettiContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10000;
  overflow: hidden;
`;

const ConfettiPiece = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  left: ${props => props.left}%;
  animation: ${confettiFall} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
`;

// Modal overlay
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  animation: fadeIn 0.3s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Modal content
const Modal = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  color: white;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
  animation: ${bounceIn} 0.6s ease-out;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 50px 50px;
    animation: ${sparkle} 2s infinite;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const Icon = styled.div`
  font-size: 80px;
  margin-bottom: 20px;
  animation: ${pulse} 1s ease-in-out infinite;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0 0 15px 0;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Message = styled.p`
  font-size: 18px;
  margin: 10px 0;
  line-height: 1.6;
  opacity: 0.95;
`;

const PointsBadge = styled.div`
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 20px;
  margin: 25px 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const PointsAmount = styled.div`
  font-size: 48px;
  font-weight: bold;
  margin: 10px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const PointsLabel = styled.div`
  font-size: 16px;
  opacity: 0.9;
  margin-top: 5px;
`;

const AuraMilestone = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin: 15px 0;
  color: #ffd700;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const AuraMilestoneCelebration = ({ milestoneValue, pointsAwarded, onClose }) => {
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    // Generate confetti pieces
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffe66d', '#ff6b9d', '#c44569', '#f8b500'];
    const pieces = [];
    
    for (let i = 0; i < 50; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        size: Math.random() * 10 + 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        duration: Math.random() * 3 + 2,
        delay: Math.random() * 2,
      });
    }
    
    setConfetti(pieces);
    
    // Auto-close after 8 seconds
    const timer = setTimeout(() => {
      if (onClose) onClose();
    }, 8000);
    
    return () => clearTimeout(timer);
  }, [onClose]);

  if (!milestoneValue) return null;

  return (
    <>
      <ConfettiContainer>
        {confetti.map(piece => (
          <ConfettiPiece
            key={piece.id}
            left={piece.left}
            size={piece.size}
            color={piece.color}
            duration={piece.duration}
            delay={piece.delay}
          />
        ))}
      </ConfettiContainer>
      
      <Overlay onClick={onClose}>
        <Modal onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose}>×</CloseButton>
          <Icon>🎉</Icon>
          <Title>Congratulations!</Title>
          <AuraMilestone>You reached {milestoneValue} Aura Points!</AuraMilestone>
          <Message>You've completed an amazing milestone!</Message>
          <PointsBadge>
            <PointsAmount>+{pointsAwarded || 20}</PointsAmount>
            <PointsLabel>Balance Points Awarded</PointsLabel>
          </PointsBadge>
          <Message style={{ fontSize: '16px', marginTop: '20px' }}>
            Keep up the great work! 🚀
          </Message>
        </Modal>
      </Overlay>
    </>
  );
};

export default AuraMilestoneCelebration;

